import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, MapPin, Clock } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { useBooking } from "../context/BookingContext";

export default function Contact() {
  const { t } = useLang();
  const { openBooking } = useBooking();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title="Contact & Booking"
        description="Connect with Shivam Chatterjee for puja, havan, spiritual guidance or Vedic astrology consultation. WhatsApp and phone: +91 83728 34728."
        path="/contact"
        keywords={["contact Tarapith tantrik", "book puja Tarapith", "Shivam Chatterjee WhatsApp"]}
      />
      <Breadcrumbs items={[{ label: t("nav_contact") }]} />

      <div className="max-w-2xl mb-10">
        <span className="text-xs font-serif tracking-[0.25em] uppercase text-tara-gold/90">{t("footer_connect")}</span>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-2 mb-3">{t("cta_speak")}</h1>
        <p className="text-tara-muted">Choose the easiest way to reach {SITE.practitioner}. Every enquiry is handled personally and respectfully.</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 mb-10">
        <motion.button
          onClick={() => openBooking()}
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="glass glass-reflect hover-lift rounded-2xl p-7 text-left"
          data-testid="contact-guided-btn"
        >
          <MessageCircle className="h-8 w-8 text-tara-gold mb-4" />
          <h2 className="font-display text-2xl font-semibold text-tara-ink mb-1">{t("cta_request")}</h2>
          <p className="text-sm text-tara-muted">Share your details in a guided form and continue on WhatsApp.</p>
        </motion.button>

        <motion.a
          href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="glass glass-reflect hover-lift rounded-2xl p-7"
          data-testid="contact-whatsapp-btn"
        >
          <MessageCircle className="h-8 w-8 text-green-600 mb-4" />
          <h2 className="font-display text-2xl font-semibold text-tara-ink mb-1">{t("cta_whatsapp")}</h2>
          <p className="text-sm text-tara-muted">{SITE.phoneDisplay}</p>
        </motion.a>
      </div>

      <div className="glass-strong rounded-2xl p-8 grid gap-6 sm:grid-cols-3">
        <div className="flex items-start gap-3">
          <Phone className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-tara-muted uppercase tracking-wider mb-1">Phone & WhatsApp</p>
            <a href={`tel:+${SITE.whatsapp}`} className="text-tara-body hover:text-tara-gold transition">{SITE.phoneDisplay}</a>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-tara-muted uppercase tracking-wider mb-1">Sacred Seat</p>
            <p className="text-tara-body">Tarapith, Birbhum, West Bengal, India</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-tara-muted uppercase tracking-wider mb-1">Response</p>
            <p className="text-tara-body">Enquiries answered personally, usually within a day.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
